import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { errorCode, errorField } from "@/lib/api-errors";
import type {
  AdminCancelSubscriptionResponse,
  AdminSubscribeResponse,
  BillingResetRequest,
  BillingResetResponse,
  CancelSubscriptionMode,
  CreditNotePreviewResponse,
  CreditNoteRequest,
  CreditNoteResponse,
  MarkInvoicePaidRequest,
  MarkInvoicePaidResponse,
  PortalLinkResponse,
  RemindInvoiceResponse,
  StripeSyncResponse,
  VoidInvoiceResponse,
} from "@/types/api";

/** Error codes of the operator billing actions that have their own message. */
const KNOWN_ERRORS = [
  "ALREADY_SUBSCRIBED",
  "NO_BILLING_CONFIG",
  "NO_SUBSCRIPTION",
  "ALREADY_CANCELED",
  "NOT_SCHEDULED_TO_CANCEL",
  "NO_STRIPE_CUSTOMER",
  "NO_DOMAIN",
  "INVOICE_NOT_OPEN",
  "INVOICE_NOT_FOUND",
  "PAYMENT_FAILED",
  "STRIPE_NOT_CONFIGURED",
  "INSTANCE_NOT_FOUND",
  "SUBSCRIPTION_LIVE",
  "CONFIRM_MISMATCH",
  "INVOICE_NOT_PAID",
  "NOTHING_TO_CREDIT",
  "AMOUNT_TOO_HIGH",
  "REFUND_NOT_POSSIBLE",
  "STRIPE_REJECTED",
  "CUSTOMER_EMAIL_MISSING",
  "RESET_WOULD_BLOCK",
  "RESUBSCRIBE_FAILED",
] as const;
type KnownError = (typeof KNOWN_ERRORS)[number];

export const isKnownError = (c: string | null): c is KnownError =>
  c !== null && (KNOWN_ERRORS as readonly string[]).includes(c);

/**
 * Everything a billing action can change: the registry (invoices +
 * subscriptions) and, for one instance, its detail, payments and audit log
 * (all keyed under ["instance", name]).
 */
export function invalidateBilling(qc: QueryClient, instanceName?: string | null) {
  qc.invalidateQueries({ queryKey: ["billing", "registry"] });
  if (instanceName) qc.invalidateQueries({ queryKey: ["instance", instanceName] });
}

function useErrorToast() {
  const { t } = useTranslation("billing");
  return (err: unknown) => {
    const code = errorCode(err);
    if (code === "PAYMENT_FAILED") {
      const url = errorField(err, "hostedInvoiceUrl");
      toast.error(t("ops.errors.PAYMENT_FAILED"), {
        description: url ? t("ops.remind.sendLinkHint", { url }) : undefined,
      });
      return;
    }
    if (code === "RESUBSCRIBE_FAILED") {
      // The reset went through, the subscription did not: the instance is
      // unsubscribed (blocked) right now. Keep the toast until dismissed.
      toast.error(t("ops.errors.RESUBSCRIBE_FAILED"), {
        description: err instanceof Error ? err.message : undefined,
        duration: Infinity,
      });
      return;
    }
    if (code === "STRIPE_REJECTED") {
      // Stripe's own reason (e.g. the refund exceeds the charge) is the useful part.
      toast.error(t("ops.errors.STRIPE_REJECTED"), {
        description: err instanceof Error ? err.message : undefined,
      });
      return;
    }
    if (isKnownError(code)) {
      toast.error(t(`ops.errors.${code}`));
      return;
    }
    toast.error(err instanceof Error ? err.message : t("ops.errors.generic"));
  };
}

/** Mark-paid + reminder for one invoice row. */
export function useInvoiceOps(invoiceId: string, instanceName: string | null) {
  const { t } = useTranslation("billing");
  const qc = useQueryClient();
  const onError = useErrorToast();
  const base = `/api/admin/billing/invoices/${encodeURIComponent(invoiceId)}`;

  const markPaid = useMutation({
    mutationFn: (body: MarkInvoicePaidRequest) =>
      api.post<MarkInvoicePaidResponse>(`${base}/mark-paid`, body),
    onSuccess: () => {
      toast.success(t("ops.markPaid.done"));
      invalidateBilling(qc, instanceName);
    },
    onError,
  });

  const remind = useMutation({
    mutationFn: () => api.post<RemindInvoiceResponse>(`${base}/remind`, {}),
    onSuccess: (res) => {
      toast.success(res.action === "charged" ? t("ops.remind.charged") : t("ops.remind.sent"));
      invalidateBilling(qc, instanceName);
    },
    onError: (err: unknown) => {
      onError(err);
      // A failed retry still changes the invoice (failure reason, audit row).
      invalidateBilling(qc, instanceName);
    },
  });

  const voidInvoice = useMutation({
    mutationFn: () => api.post<VoidInvoiceResponse>(`${base}/void`, {}),
    onSuccess: () => {
      toast.success(t("ops.void.done"));
      invalidateBilling(qc, instanceName);
    },
    onError,
  });

  return { markPaid, remind, voidInvoice };
}

/** Credit note (with or without refund) for one paid invoice. */
export function useCreditNoteOps(invoiceId: string, instanceName: string | null) {
  const { t } = useTranslation("billing");
  const qc = useQueryClient();
  const onError = useErrorToast();
  const base = `/api/admin/billing/invoices/${encodeURIComponent(invoiceId)}/credit-note`;

  const create = useMutation({
    mutationFn: (body: CreditNoteRequest) => api.post<CreditNoteResponse>(base, body),
    onSuccess: (res) => {
      toast.success(
        res.refundAmountCents > 0 ? t("ops.creditNote.doneRefund") : t("ops.creditNote.done")
      );
      invalidateBilling(qc, instanceName);
    },
    onError,
  });

  return { create };
}

/**
 * Live credit-note figures for the dialog (POST .../credit-note/preview, no
 * side effects). `body` null = disabled. Errors are shown inline, not toasted.
 */
export function useCreditNotePreview(invoiceId: string, body: CreditNoteRequest | null) {
  return useQuery({
    queryKey: ["billing", "credit-note-preview", invoiceId, body],
    queryFn: () =>
      api.post<CreditNotePreviewResponse>(
        `/api/admin/billing/invoices/${encodeURIComponent(invoiceId)}/credit-note/preview`,
        body
      ),
    enabled: body !== null,
    retry: false,
    gcTime: 0,
    placeholderData: keepPreviousData,
  });
}

/** Subscription-level actions of one instance. */
export function useSubscriptionOps(name: string) {
  const { t } = useTranslation("billing");
  const qc = useQueryClient();
  const onError = useErrorToast();
  const base = `/api/admin/instances/${encodeURIComponent(name)}/billing`;
  const done = (message: string) => () => {
    toast.success(message);
    invalidateBilling(qc, name);
  };

  const subscribe = useMutation({
    mutationFn: (idemKey: string) =>
      api.post<AdminSubscribeResponse>(`${base}/subscribe`, { idemKey }),
    onSuccess: done(t("ops.subscribe.done")),
    onError,
  });

  const cancel = useMutation({
    mutationFn: (mode: CancelSubscriptionMode) =>
      api.post<AdminCancelSubscriptionResponse>(`${base}/cancel`, { mode }),
    onSuccess: (_res, mode) =>
      done(mode === "immediate" ? t("ops.cancel.doneImmediate") : t("ops.cancel.donePeriodEnd"))(),
    onError,
  });

  const resume = useMutation({
    mutationFn: () => api.post<{ ok: true }>(`${base}/resume`, {}),
    onSuccess: done(t("ops.resume.done")),
    onError,
  });

  const portalLink = useMutation({
    mutationFn: () => api.post<PortalLinkResponse>(`${base}/portal-link`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["instance", name, "billing-audit"] }),
    onError,
  });

  const sync = useMutation({
    mutationFn: () => api.post<StripeSyncResponse>(`${base}/sync`, {}),
    onSuccess: (res) => {
      toast.success(t("ops.sync.done", { count: res.invoicesRecorded }));
      invalidateBilling(qc, name);
    },
    onError,
  });

  const reset = useMutation({
    mutationFn: (body: BillingResetRequest) =>
      api.post<BillingResetResponse>(`${base}/reset`, body),
    onSuccess: (res) => {
      toast.success(
        res.subscriptionId
          ? t("ops.reset.doneResubscribed", { count: res.archivedPayments })
          : t("ops.reset.done", { count: res.archivedPayments })
      );
      invalidateBilling(qc, name);
    },
    onError: (err: unknown) => {
      onError(err);
      // A failed resubscribe still reset the instance.
      if (errorCode(err) === "RESUBSCRIBE_FAILED") invalidateBilling(qc, name);
    },
  });

  return { subscribe, cancel, resume, portalLink, sync, reset };
}
